import { useQueries, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { Badge } from '../ui/Badge.jsx';
import { Spinner } from '../ui/Spinner.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { salesApi } from '../../services/salesApi.js';

const STATUS_VARIANT = {
  DRAFT: 'neutral', SENT: 'info', ACCEPTED: 'success', REJECTED: 'danger',
  CONFIRMED: 'info', FULFILLED: 'success', CANCELLED: 'neutral',
  DELIVERED: 'success', SHIPPED: 'info', PENDING: 'warning',
  POSTED: 'info', PAID: 'success', REQUESTED: 'warning', APPROVED: 'info', RECEIVED: 'success',
};
const sv = (s) => STATUS_VARIANT[s] || 'neutral';

/**
 * Belegfluss: the linked document chain for a sales order -
 * quote → order → deliveries → invoice(s) → credit notes / returns.
 * Pure frontend aggregation over existing endpoints (RBAC-tolerant).
 */
export function DocumentFlow({ order }) {
  const { t } = useTranslation();

  const quote = useQuery({
    queryKey: ['flow', 'quote', order.quoteId],
    queryFn: () => salesApi.getQuote(order.quoteId).then((r) => r.items),
    enabled: Boolean(order.quoteId),
    retry: false,
  });
  const deliveries = useQuery({
    queryKey: ['flow', 'deliveries', order.id],
    queryFn: () => salesApi.listOrderDeliveries(order.id).then((r) => r.items || []),
    retry: false,
  });
  const invoicesQ = useQuery({
    queryKey: ['flow', 'invoices'],
    queryFn: () => financeApi.listInvoices({ limit: 200 }).then((r) => r.items),
    retry: false,
  });
  const returnsQ = useQuery({
    queryKey: ['flow', 'returns'],
    queryFn: () => salesApi.listReturns().then((r) => r.items),
    retry: false,
  });

  const invoices = (invoicesQ.data || []).filter((i) => i.sourceType === 'SALES_ORDER' && i.sourceId === order.id);
  const returns = (returnsQ.data || []).filter((r) => r.orderId === order.id);

  const creditNoteQs = useQueries({
    queries: invoices.map((inv) => ({
      queryKey: ['flow', 'cn', inv.id],
      queryFn: () => financeApi.listInvoiceCreditNotes(inv.id).then((r) => r.items || []),
      retry: false,
    })),
  });
  const creditNotes = creditNoteQs.flatMap((q) => q.data || []);

  const loading = deliveries.isLoading || invoicesQ.isLoading || returnsQ.isLoading;

  const stages = [];
  if (order.quoteId) {
    stages.push({ key: 'quote', label: t('flow.quote'), docs: quote.data ? [{ number: quote.data.quoteNumber, status: quote.data.status, to: `/sales/quotes/${quote.data.id}` }] : [] });
  }
  stages.push({ key: 'order', label: t('flow.order'), current: true, docs: [{ number: order.orderNumber, status: order.status, to: `/sales/orders/${order.id}` }] });
  stages.push({ key: 'delivery', label: t('flow.delivery'), docs: (deliveries.data || []).map((d) => ({ number: d.deliveryNumber, status: d.status, to: '/sales/deliveries' })) });
  stages.push({ key: 'invoice', label: t('flow.invoice'), docs: invoices.map((i) => ({ number: i.invoiceNumber, status: i.status, to: `/finance/invoices/${i.id}` })) });
  if (creditNotes.length) stages.push({ key: 'creditNote', label: t('flow.creditNote'), docs: creditNotes.map((c) => ({ number: c.creditNoteNumber, status: c.status, to: '/finance/credit-notes' })) });
  if (returns.length) stages.push({ key: 'return', label: t('flow.return'), docs: returns.map((r) => ({ number: r.returnNumber, status: r.status, to: '/sales/returns' })) });

  return (
    <div className="doc-flow">
      {loading ? <Spinner /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
          {stages.map((stage, si) => (
            <div key={stage.key}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)', padding: 'var(--space-1) 0' }}>
                <div className="muted text-sm" style={{ width: 110, flexShrink: 0, fontWeight: stage.current ? 700 : 400 }}>{stage.label}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  {stage.docs.length === 0
                    ? <span className="muted text-sm">{t('flow.none')}</span>
                    : stage.docs.map((d) => (
                      <Link key={d.number} to={d.to} style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <span className="mono" style={{ fontWeight: stage.current ? 700 : 500 }}>{d.number}</span>
                        {d.status && <Badge variant={sv(d.status)}>{d.status}</Badge>}
                      </Link>
                    ))}
                </div>
              </div>
              {si < stages.length - 1 && <div style={{ marginLeft: 110 + 12, color: 'var(--color-border)' }}>↓</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
